import { supabase } from '../lib/supabase';

/**
 * Fetch owner's active trailer listings
 * @param {string} ownerId - Owner user ID
 * @returns {Promise<Array>} Array of trailer listings
 */
export const fetchOwnerListings = async (ownerId) => {
  try {
    const { data, error } = await supabase
      ?.from('trailer_listings')
      ?.select('*')
      ?.eq('owner_id', ownerId)
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching owner listings:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchOwnerListings:', error);
    return [];
  }
};

/**
 * Fetch trailer listing by ID
 * @param {string} id - Trailer listing ID
 * @returns {Promise<Object|null>} Trailer listing data
 */
export const fetchTrailerListingById = async (id) => {
  try {
    const { data, error } = await supabase
      ?.from('trailer_listings')
      ?.select(`
        *,
        owner:user_profiles!trailer_listings_owner_id_fkey(
          id,
          full_name,
          email,
          phone,
          profile_photo_url,
          verified_status,
          city,
          state,
          bio,
          created_at
        )
      `)
      ?.eq('id', id)
      ?.single();

    if (error) {
      console.error('Error fetching trailer listing:', error?.message);
      return null;
    }

    const listing = convertToCamelCase(data);

    // Fetch owner's total listing count
    if (listing?.ownerId) {
      const { count: listingCount } = await supabase
        ?.from('trailer_listings')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('owner_id', listing?.ownerId)
        ?.eq('status', 'active');

      listing.ownerListingCount = listingCount || 1;

      // Fetch owner's average rating and review count from trailer_reviews
      const { data: reviewStats } = await supabase
        ?.from('trailer_reviews')
        ?.select('overall_rating')
        ?.eq('reviewed_user_id', listing?.ownerId)
        ?.eq('review_type', 'renter_to_owner');

      if (reviewStats && reviewStats?.length > 0) {
        const avg = reviewStats?.reduce((sum, r) => sum + (r?.overall_rating || 0), 0) / reviewStats?.length;
        listing.ownerAverageRating = parseFloat(avg?.toFixed(1));
        listing.ownerReviewCount = reviewStats?.length;
      } else {
        listing.ownerAverageRating = 0;
        listing.ownerReviewCount = 0;
      }
    }

    return listing;
  } catch (error) {
    console.error('Error in fetchTrailerListingById:', error);
    return null;
  }
};

/**
 * Fetch reviews for a trailer listing
 * @param {string} trailerId - Trailer listing ID
 * @returns {Promise<Array>} Array of reviews
 */
export const fetchTrailerReviews = async (trailerId) => {
  try {
    const { data, error } = await supabase
      ?.from('trailer_reviews')
      ?.select(`
        *,
        reviewer:user_profiles!trailer_reviews_reviewer_id_fkey(
          id,
          full_name,
          profile_photo_url
        )
      `)
      ?.eq('trailer_listing_id', trailerId)
      ?.eq('review_type', 'renter_to_trailer')
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching trailer reviews:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchTrailerReviews:', error);
    return [];
  }
};

/**
 * Fetch active trailer listings with filters
 * @param {Object} filters - Search filters
 * @returns {Promise<Array>} Array of trailer listings
 */
export const fetchActiveTrailerListings = async (filters = {}) => {
  try {
    let query = supabase
      ?.from('trailer_listings')
      ?.select('*')
      ?.eq('status', 'active');

    // Apply filters
    if (filters?.location) {
      query = query?.or(`location_city.ilike.%${filters?.location}%,location_state.ilike.%${filters?.location}%`);
    }

    if (filters?.trailerType && filters?.trailerType !== 'all') {
      query = query?.eq('trailer_type', filters?.trailerType);
    }

    if (filters?.minPrice) {
      query = query?.gte('daily_rate', filters?.minPrice);
    }

    if (filters?.maxPrice) {
      query = query?.lte('daily_rate', filters?.maxPrice);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching active trailer listings:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchActiveTrailerListings:', error);
    return [];
  }
};

/**
 * Fetch favorite trailers for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of favorite trailers
 */
export const fetchFavoriteTrailers = async (userId) => {
  try {
    // Note: This assumes a favorites table exists. If not, return empty array.
    // For now, returning empty array as favorites table was not found in schema
    console.log('Favorites feature not yet implemented - no favorites table found');
    return [];
  } catch (error) {
    console.error('Error in fetchFavoriteTrailers:', error);
    return [];
  }
};

/**
 * Create a new trailer listing
 * @param {Object} listingData - Trailer listing data
 * @returns {Promise<Object|null>} Created listing
 */
export const createTrailerListing = async (listingData) => {
  try {
    const { data, error } = await supabase
      ?.from('trailer_listings')
      ?.insert([listingData])
      ?.select()
      ?.single();

    if (error) {
      console.error('Error creating trailer listing:', error?.message);
      throw error;
    }

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error in createTrailerListing:', error);
    throw error;
  }
};

/**
 * Update an existing trailer listing
 * @param {string} listingId - Listing ID
 * @param {Object} updates - Updated listing data
 * @returns {Promise<Object|null>} Updated listing
 */
export const updateTrailerListing = async (listingId, updates) => {
  try {
    const { data, error } = await supabase
      ?.from('trailer_listings')
      ?.update(updates)
      ?.eq('id', listingId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating trailer listing:', error?.message);
      throw error;
    }

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error in updateTrailerListing:', error);
    throw error;
  }
};

/**
 * Delete a trailer listing (soft delete - sets status to 'deleted')
 * @param {string} listingId - Listing ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteTrailerListing = async (listingId) => {
  try {
    const { error } = await supabase
      ?.from('trailer_listings')
      ?.update({ status: 'deleted' })
      ?.eq('id', listingId);

    if (error) {
      console.error('Error deleting trailer listing:', error?.message);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteTrailerListing:', error);
    throw error;
  }
};

/**
 * Fetch owner's deleted trailer listings
 * @param {string} ownerId - Owner user ID
 * @returns {Promise<Array>} Array of deleted trailer listings
 */
export const fetchDeletedListings = async (ownerId) => {
  try {
    const { data, error } = await supabase
      ?.from('trailer_listings')
      ?.select('*')
      ?.eq('owner_id', ownerId)
      ?.eq('status', 'deleted')
      ?.order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching deleted listings:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchDeletedListings:', error);
    return [];
  }
};

/**
 * Reactivate a deleted trailer listing
 * @param {string} listingId - Listing ID
 * @returns {Promise<Object|null>} Updated listing
 */
export const reactivateListing = async (listingId) => {
  try {
    const { data, error } = await supabase
      ?.from('trailer_listings')
      ?.update({ status: 'inactive' })
      ?.eq('id', listingId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error reactivating listing:', error?.message);
      throw error;
    }

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error in reactivateListing:', error);
    throw error;
  }
};

/**
 * Toggle listing status (active/inactive)
 * @param {string} listingId - Listing ID
 * @param {string} status - New status
 * @returns {Promise<Object|null>} Updated listing
 */
export const toggleListingStatus = async (listingId, status) => {
  try {
    const { data, error } = await supabase
      ?.from('trailer_listings')
      ?.update({ status })
      ?.eq('id', listingId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error toggling listing status:', error?.message);
      throw error;
    }

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error in toggleListingStatus:', error);
    throw error;
  }
};

/**
 * Convert snake_case to camelCase
 */
function convertToCamelCase(obj) {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(convertToCamelCase);
  if (typeof obj !== 'object') return obj;

  const camelCaseObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelCaseObj[camelKey] = typeof value === 'object' ? convertToCamelCase(value) : value;
  }
  return camelCaseObj;
}

export const trailerListingService = {
  fetchOwnerListings,
  fetchTrailerListingById,
  fetchActiveTrailerListings,
  fetchFavoriteTrailers,
  createTrailerListing,
  updateTrailerListing,
  deleteTrailerListing,
  toggleListingStatus,
  fetchDeletedListings,
  reactivateListing
};

/**
 * Calculate distance between two lat/lng points using Haversine formula
 * @returns distance in miles
 */
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Geocode a US zipcode to lat/lng using zippopotam.us (no API key required)
 * @param {string} zip - US zipcode
 * @returns {Promise<{lat: number, lng: number, city: string, state: string}|null>}
 */
async function geocodeZipcode(zip) {
  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!response?.ok) return null;
    const data = await response?.json();
    if (!data?.places?.[0]) return null;
    return {
      lat: parseFloat(data?.places?.[0]?.latitude),
      lng: parseFloat(data?.places?.[0]?.longitude),
      city: data?.places?.[0]?.['place name'],
      state: data?.places?.[0]?.['state abbreviation']
    };
  } catch {
    return null;
  }
}

/**
 * Fetch trailer listings with search filters (location-based search)
 * Supports 25-mile radius search when a zipcode is entered.
 * @param {Object} filters - Search filters including location, trailerType, minPrice, maxPrice, pickupDate, returnDate
 * @returns {Promise<Array>} Array of trailer listings
 */
async function fetchTrailerListings(filters = {}) {
  try {
    let query = supabase
      ?.from('trailer_listings')
      ?.select(`
        *,
        owner:user_profiles!trailer_listings_owner_id_fkey(
          id,
          full_name,
          profile_photo_url,
          verified_status
        )
      `)
      ?.eq('status', 'active');

    // Trailer type filter
    if (filters?.trailerType && filters?.trailerType !== '' && filters?.trailerType !== 'all') {
      query = query?.eq('trailer_type', filters?.trailerType);
    }

    // Price range filters
    if (filters?.minPrice && filters?.minPrice !== '') {
      query = query?.gte('daily_rate', parseFloat(filters?.minPrice));
    }

    if (filters?.maxPrice && filters?.maxPrice !== '') {
      query = query?.lte('daily_rate', parseFloat(filters?.maxPrice));
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching trailer listings:', error?.message);
      return [];
    }

    let listings = data?.map(convertToCamelCase) || [];

    // Apply location filtering
    if (filters?.location && filters?.location?.trim() !== '') {
      const loc = filters?.location?.trim();
      const isZipcode = /^\d{5}(-\d{4})?$/?.test(loc);

      if (isZipcode) {
        // Geocode the zipcode and filter by 25-mile radius
        const geoResult = await geocodeZipcode(loc);

        if (geoResult) {
          const RADIUS_MILES = 25;
          listings = listings?.filter((trailer) => {
            // If trailer has coordinates, use Haversine distance
            if (trailer?.locationLatitude && trailer?.locationLongitude) {
              const dist = haversineDistance(
                geoResult?.lat,
                geoResult?.lng,
                parseFloat(trailer?.locationLatitude),
                parseFloat(trailer?.locationLongitude)
              );
              return dist <= RADIUS_MILES;
            }
            // Fallback: match by zip, city, or state text if no coordinates stored
            const zipMatch = trailer?.locationZip === loc;
            const cityMatch = trailer?.locationCity?.toLowerCase() === geoResult?.city?.toLowerCase();
            const stateMatch = trailer?.locationState?.toUpperCase() === geoResult?.state?.toUpperCase();
            return zipMatch || cityMatch || stateMatch;
          });

          // Attach distance to each listing for display/sorting
          listings = listings?.map((trailer) => {
            if (trailer?.locationLatitude && trailer?.locationLongitude) {
              const dist = haversineDistance(
                geoResult?.lat,
                geoResult?.lng,
                parseFloat(trailer?.locationLatitude),
                parseFloat(trailer?.locationLongitude)
              );
              return { ...trailer, distanceMiles: Math.round(dist * 10) / 10 };
            }
            return trailer;
          });

          // Sort by distance ascending
          listings?.sort((a, b) => (a?.distanceMiles ?? 999) - (b?.distanceMiles ?? 999));
        } else {
          // Geocoding failed — fall back to zip text match
          listings = listings?.filter((trailer) =>
            trailer?.locationZip?.includes(loc)
          );
        }
      } else {
        // Not a zipcode — text match on city, state, or zip
        const locLower = loc?.toLowerCase();
        listings = listings?.filter((trailer) =>
          trailer?.locationCity?.toLowerCase()?.includes(locLower) ||
          trailer?.locationState?.toLowerCase()?.includes(locLower) ||
          trailer?.locationZip?.toLowerCase()?.includes(locLower)
        );
      }
    }

    return listings;
  } catch (error) {
    console.error('Error in fetchTrailerListings:', error);
    return [];
  }
}

export { fetchTrailerListings };